import { messageFor, type ApiErrorBody } from '@foundation/shared';
import { readOrgLocation } from './orgContext';

const TOKEN_KEY = 'foundation.admin.token';

export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly details?: unknown,
    serverMessage?: string,
  ) {
    // ข้อความจากเซิร์ฟเวอร์เขียนไว้ให้คนอ่านและบอกวิธีแก้ (เช่น "ยังไม่ได้ตั้ง
    // access token ของ OA เจ้าหน้าที่") ส่วน messageFor(code) เป็นข้อความรวม ๆ
    // ต่อ code เท่านั้น — ทิ้งของเซิร์ฟเวอร์ไปแล้วผู้ใช้จะเห็นแต่ "เกิดข้อผิดพลาด
    // ในระบบ" ซึ่งไม่ช่วยอะไรเลย
    //
    // ปลอดภัยเพราะ error ที่ไม่ได้ตั้งใจจะถูก error handler กลางแปลงเป็นข้อความ
    // รวม ๆ ตั้งแต่ฝั่งเซิร์ฟเวอร์แล้ว รายละเอียดภายในไม่หลุดออกมาถึงตรงนี้
    super(serverMessage?.trim() || messageFor(code));
    this.name = 'ApiRequestError';
  }
}

export const tokenStore = {
  get: () => sessionStorage.getItem(TOKEN_KEY),
  set: (token: string) => sessionStorage.setItem(TOKEN_KEY, token),
  clear: () => sessionStorage.removeItem(TOKEN_KEY),
};

/** callback ที่ AuthProvider ตั้งไว้ เพื่อเด้งกลับหน้า login เมื่อ token หมดอายุ */
let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: () => void): void {
  onUnauthenticated = fn;
}

/**
 * ที่อยู่ของ API
 *
 * ตอน dev ใช้ '/api' แล้วให้ Vite proxy ไปที่ Worker — จะได้ไม่ต้องตั้ง CORS
 * ตอน production เว็บอยู่บน Pages ส่วน API อยู่บน Workers คนละ origin กัน
 * จึงต้องตั้ง VITE_API_BASE ตอน build เป็น URL เต็มของ Worker
 * (และใส่ origin ของเว็บลง ALLOWED_ORIGINS ฝั่ง Worker ด้วย ไม่งั้น CORS จะบล็อก)
 */
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** สร้าง key อัตโนมัติสำหรับ POST ที่สร้างข้อมูล */
  idempotent?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = tokenStore.get();
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (opts.idempotent) headers['idempotency-key'] = crypto.randomUUID();
  // บอกว่าเป็นมูลนิธิไหน เฉพาะเมื่อลิงก์ระบุมาจริง — ส่ง slug ที่เดาเอง
  // จะไปทับการหาจากโดเมนของมูลนิธิที่ตั้งโดเมนตัวเองไว้
  const loc = readOrgLocation();
  if (loc.explicit) headers['x-org'] = loc.slug;

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const data: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = data as ApiErrorBody;
    const code = err?.error?.code ?? 'INTERNAL_ERROR';
    if (code === 'UNAUTHENTICATED' || code === 'TOKEN_EXPIRED') {
      tokenStore.clear();
      onUnauthenticated?.();
    }
    throw new ApiRequestError(code, res.status, err?.error?.details, err?.error?.message);
  }

  return data as T;
}

/**
 * ดึงไฟล์ (CSV/PDF) ที่ต้องแนบ token
 *
 * เปิด URL ตรง ๆ ด้วย <a href> ไม่ได้เพราะ endpoint ต้องการ Authorization header
 * และการใส่ token ลง query string จะทำให้มันไปโผล่ใน log ของ proxy
 */
export async function apiBlob(
  path: string,
): Promise<{ blob: Blob; rowCount: number; truncated: boolean }> {
  const token = tokenStore.get();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let code = 'INTERNAL_ERROR';
    let message: string | undefined;
    try {
      const parsed = (JSON.parse(text) as ApiErrorBody)?.error;
      code = parsed?.code ?? code;
      message = parsed?.message;
    } catch {
      /* ไม่ใช่ JSON — ใช้ code ปริยาย */
    }
    if (code === 'UNAUTHENTICATED' || code === 'TOKEN_EXPIRED') {
      tokenStore.clear();
      onUnauthenticated?.();
    }
    throw new ApiRequestError(code, res.status, undefined, message);
  }

  return {
    blob: await res.blob(),
    rowCount: Number(res.headers.get('x-row-count') ?? '0'),
    truncated: res.headers.get('x-truncated') === 'true',
  };
}

export function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/**
 * เปิดเอกสาร HTML ที่ต้องใช้ Authorization header (เช่นใบอนุโมทนาบัตร)
 *
 * `<a href>` ตรง ๆ ใช้ไม่ได้เพราะเบราว์เซอร์ไม่ส่ง token ไปด้วย และการยัด token
 * ลง query string จะทำให้มันไปโผล่ใน log ของตัวกลาง — ดึงมาเป็น blob แล้วเปิดแทน
 */
export async function openAuthedDocument(
  path: string,
  opts: { download?: string } = {},
): Promise<void> {
  const token = tokenStore.get();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let code = 'INTERNAL_ERROR';
    let message: string | undefined;
    try {
      const parsed = (JSON.parse(text) as ApiErrorBody)?.error;
      code = parsed?.code ?? code;
      message = parsed?.message;
    } catch {
      /* ไม่ใช่ JSON — ใช้ code ปริยาย */
    }
    if (code === 'UNAUTHENTICATED' || code === 'TOKEN_EXPIRED') {
      tokenStore.clear();
      onUnauthenticated?.();
    }
    throw new ApiRequestError(code, res.status, undefined, message);
  }

  const url = URL.createObjectURL(await res.blob());
  if (opts.download) {
    const a = document.createElement('a');
    a.href = url;
    a.download = opts.download;
    a.click();
  } else {
    window.open(url, '_blank', 'noopener');
  }
  // ให้เวลาแท็บใหม่โหลดก่อนคืนหน่วยความจำ — revoke ทันทีจะได้หน้าว่าง
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
